import React, { useState } from "react";
import {
	Dialog,
	DialogTitle,
	DialogContent,
	DialogActions,
	TextField,
	Button,
	Typography,
	Alert,
	Box,
	CircularProgress,
} from "@mui/material";

interface ContactSalesModalProps {
	open: boolean;
	onClose: () => void;
	/**
	 * Optional context attached to the inquiry email (e.g. "From Pricing
	 * page — clicked Contact Sales while viewing Premium tier"). Helps the
	 * sales team triage incoming requests.
	 */
	context?: string;
}

const MAX_MESSAGE_LENGTH = 4000;

const ContactSalesModal: React.FC<ContactSalesModalProps> = ({
	open,
	onClose,
	context,
}) => {
	const [name, setName] = useState("");
	const [email, setEmail] = useState("");
	const [company, setCompany] = useState("");
	const [message, setMessage] = useState("");
	const [submitting, setSubmitting] = useState(false);
	const [error, setError] = useState<string | null>(null);
	const [success, setSuccess] = useState<string | null>(null);

	const reset = () => {
		setName("");
		setEmail("");
		setCompany("");
		setMessage("");
		setError(null);
		setSuccess(null);
		setSubmitting(false);
	};

	const handleClose = () => {
		if (submitting) return;
		reset();
		onClose();
	};

	const handleSubmit = async (e: React.FormEvent) => {
		e.preventDefault();
		setError(null);
		setSuccess(null);

		if (!name.trim() || !email.trim() || !message.trim()) {
			setError("Name, email, and message are required.");
			return;
		}

		setSubmitting(true);
		try {
			const res = await fetch(
				`${process.env.REACT_APP_LAMBDA_ARTEH_API_DOMAIN}/api/contact-sales`,
				{
					method: "POST",
					headers: { "Content-Type": "application/json" },
					body: JSON.stringify({
						name: name.trim(),
						email: email.trim(),
						company: company.trim(),
						message: message.trim(),
						context: context || "",
					}),
				},
			);

			const data = await res.json().catch(() => ({}));
			if (!res.ok) {
				throw new Error(
					data?.Message ||
						data?.error ||
						`Request failed (${res.status})`,
				);
			}

			setSuccess(
				data?.Data?.message ||
					"Thanks — your message is on the way. Our sales team will reply within one business day.",
			);
		} catch (err: any) {
			console.error("Contact sales submit failed:", err);
			setError(
				err.message ||
					"Could not submit your inquiry. Please try again or email sales@arteh.com.au.",
			);
		} finally {
			setSubmitting(false);
		}
	};

	return (
		<Dialog
			open={open}
			onClose={handleClose}
			maxWidth="sm"
			fullWidth
			PaperProps={{ sx: { borderRadius: 3 } }}
		>
			<DialogTitle
				sx={{
					pb: 1,
					fontWeight: 600,
					color: "#2c3e50",
					fontSize: "1.5rem",
				}}
			>
				Talk to sales
				<Typography
					variant="body2"
					component="div"
					sx={{ color: "#6c757d", mt: 0.5, fontWeight: 400 }}
				>
					Tell us about your needs and we'll be in touch within one
					business day.
				</Typography>
			</DialogTitle>

			<form onSubmit={handleSubmit}>
				<DialogContent sx={{ pt: 1 }}>
					{error && (
						<Alert severity="error" sx={{ mb: 2 }}>
							{error}
						</Alert>
					)}
					{success && (
						<Alert severity="success" sx={{ mb: 2 }}>
							{success}
						</Alert>
					)}

					{!success && (
						<Box
							sx={{
								display: "flex",
								flexDirection: "column",
								gap: 2,
							}}
						>
							<TextField
								label="Your name"
								value={name}
								onChange={(e) => setName(e.target.value)}
								required
								fullWidth
								disabled={submitting}
								autoComplete="name"
							/>
							<TextField
								label="Email"
								type="email"
								value={email}
								onChange={(e) => setEmail(e.target.value)}
								required
								fullWidth
								disabled={submitting}
								autoComplete="email"
							/>
							<TextField
								label="Company"
								value={company}
								onChange={(e) => setCompany(e.target.value)}
								fullWidth
								disabled={submitting}
								autoComplete="organization"
							/>
							<TextField
								label="Message"
								value={message}
								onChange={(e) =>
									setMessage(
										e.target.value.slice(
											0,
											MAX_MESSAGE_LENGTH,
										),
									)
								}
								required
								fullWidth
								multiline
								minRows={4}
								disabled={submitting}
								helperText={`${message.length} / ${MAX_MESSAGE_LENGTH}`}
								placeholder="Briefly describe what you're looking for — billing terms, integrations, team size, etc."
							/>
						</Box>
					)}
				</DialogContent>

				<DialogActions sx={{ p: 3, pt: 1 }}>
					<Button
						onClick={handleClose}
						disabled={submitting}
						sx={{
							color: "#6c757d",
							textTransform: "none",
						}}
					>
						{success ? "Close" : "Cancel"}
					</Button>
					{!success && (
						<Button
							type="submit"
							variant="contained"
							disabled={submitting}
							sx={{
								background:
									"linear-gradient(135deg, #5DBEC3 0%, #4BA3A7 100%)",
								color: "#fff",
								textTransform: "none",
								px: 3,
								"&:hover": {
									background:
										"linear-gradient(135deg, #4BA3A7 0%, #3A8F92 100%)",
								},
							}}
						>
							{submitting ? (
								<CircularProgress
									size={20}
									sx={{ color: "#fff" }}
								/>
							) : (
								"Send inquiry"
							)}
						</Button>
					)}
				</DialogActions>
			</form>
		</Dialog>
	);
};

export default ContactSalesModal;
